import { gql } from '@apollo/client';
import { useCallback, useState } from 'react';
import { apolloClient } from '../apollo';

// The platform's file-upload hook. Not authored surface: restored from the template on every sync,
// and unwritable under `src/lib/platform/`.
//
// The upload itself never touches the generated backend: the browser mints a ticket through the
// mutation below, then POSTs the bytes same-origin, straight to the parent api-runtime — the
// generated app authors nothing that ever holds a file or a byte count. XHR, not `fetch`, is the
// one deliberate choice here: `fetch` has no upload-progress event, and a multi-megabyte file with
// no progress reads as a hung page.
//
// `UPLOAD_FILE` / `UPLOAD_FILE_STATUS` below are hand-written and stay that way. This module ships
// in EVERY app, and `@/lib/gql/__generated__` only ever holds documents for operations that app's
// own schema declares — an app whose design marks no act as handing a file over has no `uploadFile`
// at all, so a generated import here would fail to resolve in most apps that carry this file. The
// contract they are written against is the platform's, not this app's: `clientSurfaceApi('uploadFile')`
// in `@on.auto/integration-catalog` declares both operations, their arguments and both response shapes, and
// `platform-uploads-contract.fence.test.ts` pins these two documents against it name for name.

const UPLOAD_FILE = gql`
  mutation UploadFile($input: UploadFileInput!) {
    uploadFile(input: $input) {
      fileId
      ticket
    }
  }
`;

const UPLOAD_FILE_STATUS = gql`
  query UploadFileStatus($fileId: String!) {
    uploadFileStatus(fileId: $fileId) {
      id
      fileName
      contentType
      byteSize
      checksum
      status
    }
  }
`;

/**
 * THE PLATFORM'S OWN WORDS for an upload record, as the status query answers with them.
 *
 * Spelled here rather than imported because this module ships INTO a generated app, which carries
 * none of the platform's packages — so these three are a COPY of the client-surface catalog's
 * `gate.statusValues`, and `platform-uploads-contract.fence.test.ts` holds the copy to its original,
 * along with both operation names and every field of both responses. A rename there turns that fence
 * red, which is the only thing that can: nothing in a generated app resolves these words at build time.
 */
export const UPLOAD_RECORD_STATUS = { pending: 'pending', settled: 'stored', refused: 'rejected' } as const;

/**
 * WHAT A PAGE READS, AND WHICH OF ITS FIVE VALUES BELONG TO THE RECORD.
 *
 * TWO are the record's own. `'stored'` is when `ref` is a handle. `'rejected'` is when none is
 * coming and `error` holds the reason — deliberately WIDER here than on the record, which can only
 * speak about bytes it received: a mint the server would not issue, a connection that dropped and a
 * record that never settled inside the poll's bound are all the same answer to a page, and nothing
 * it can do differs between them.
 *
 * THE OTHER THREE ARE THE BROWSER'S and belong to no contract: `'idle'` before a file is picked,
 * `'uploading'` from the pick until the bytes are handed over — which begins before the record
 * exists and then runs alongside its `'pending'` — and `'settling'` while the platform decides. None
 * of the three yields a `ref`.
 */
export type UploadStatus =
  | 'idle'
  | 'uploading'
  | 'settling'
  | typeof UPLOAD_RECORD_STATUS.settled
  | typeof UPLOAD_RECORD_STATUS.refused;

export interface FileUploadState {
  pick(file: File): Promise<void>;
  /** A fraction 0..1 while the bytes are in flight; `null` before a pick and once settling starts. */
  progress: number | null;
  /** The stored file's id, once `status` reaches `'stored'` — what a mutation's own variables carry
   *  as the file reference. */
  ref: string | null;
  status: UploadStatus;
  error: string | null;
  reset(): void;
}

interface UploadPatch {
  status?: UploadStatus;
  progress?: number | null;
  ref?: string | null;
  error?: string | null;
}

/** How many times `pollStatus` is asked before giving up — not a wall-clock bound, so a fake
 *  `sleep` in a test still exercises the real count instead of needing to wait it out. */
const POLL_MAX_ATTEMPTS = 8;
const POLL_BASE_DELAY_MS = 300;
const POLL_MAX_DELAY_MS = 3_000;

/** The seam a test replaces: every network and timing effect `runFileUpload` performs, so its
 *  orchestration — mint, post, poll-with-backoff, every failure landing in `error` — is exercised
 *  without a real Apollo client, a real XHR, or a real clock. */
export interface UploadDeps {
  mint: (file: File) => Promise<unknown>;
  postBytes: (ticket: string, file: File, sha256: string, onProgress: (fraction: number) => void) => Promise<unknown>;
  pollStatus: (fileId: string) => Promise<unknown>;
  sha256: (file: File) => Promise<string>;
  sleep: (ms: number) => Promise<void>;
}

/** A named string field off a response this package does not yet have a generated type for —
 *  read defensively, because nothing here trusts a schema that doesn't exist yet. */
function readString(value: unknown, key: string): string | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const field = (value as Record<string, unknown>)[key];
  return typeof field === 'string' ? field : undefined;
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : 'the upload failed';
}

function hex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** The `uploadFile` mutation: mints the ticket the browser then presents to the storage plane. */
export async function mintUploadTicket(file: File): Promise<unknown> {
  const result = await apolloClient.mutate({
    mutation: UPLOAD_FILE,
    variables: { input: { fileName: file.name, contentType: file.type, byteSize: file.size } },
  });
  return (result.data as Record<string, unknown> | null | undefined)?.uploadFile;
}

/**
 * `POST /api/uploads/:ticket`, same-origin, straight to the parent api-runtime — never the
 * generated backend. XHR rather than `fetch`: only XHR fires `upload.onprogress`, and `content-length`
 * comes for free off the `File` body XHR sends. The sha256 header lets R2 verify the bytes it
 * received against the caller's own digest, rather than the platform trusting the claim blind.
 */
export function postUploadBytes(
  ticket: string,
  file: File,
  sha256: string,
  onProgress: (fraction: number) => void,
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/uploads/${ticket}`);
    xhr.setRequestHeader('x-onauto-file-name', file.name);
    xhr.setRequestHeader('x-onauto-sha256', sha256);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error('the upload answered with something that was not JSON'));
        }
      } else {
        reject(new Error(`the upload was refused (${String(xhr.status)})`));
      }
    };
    xhr.onerror = () => reject(new Error('the upload failed — the connection dropped'));
    xhr.send(file);
  });
}

/** The `uploadFileStatus` query, one poll. `network-only`: a cached `'uploading'` answer would
 *  never let the poll see the app move to `'stored'`. */
export async function pollUploadStatus(fileId: string): Promise<unknown> {
  const result = await apolloClient.query({
    query: UPLOAD_FILE_STATUS,
    variables: { fileId },
    fetchPolicy: 'network-only',
  });
  return (result.data as Record<string, unknown> | null | undefined)?.uploadFileStatus;
}

/** The file's own sha256, hex-encoded, over `SubtleCrypto` — the same API in the browser and in
 *  Node, so this needs no platform-specific hashing library. */
export async function sha256OfFile(file: File): Promise<string> {
  return hex(await crypto.subtle.digest('SHA-256', await file.arrayBuffer()));
}

const defaultDeps: UploadDeps = {
  mint: mintUploadTicket,
  postBytes: postUploadBytes,
  pollStatus: pollUploadStatus,
  sha256: sha256OfFile,
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
};

/**
 * The whole pipeline, as one plain function: mint a ticket, POST the bytes, poll until the app
 * reports them stored. Every failure lands in `error` via `patch` — NEVER a throw, because a hook
 * built on this must never take a render down over a refused upload.
 */
export async function runFileUpload(
  file: File,
  patch: (next: UploadPatch) => void,
  deps: UploadDeps = defaultDeps,
): Promise<void> {
  patch({ status: 'uploading', progress: 0, ref: null, error: null });

  let ticket: string;
  let fileId: string;
  try {
    const minted = await deps.mint(file);
    const mintedFileId = readString(minted, 'fileId');
    const mintedTicket = readString(minted, 'ticket');
    if (mintedFileId === undefined || mintedTicket === undefined) {
      throw new Error('the server did not answer with a file id and a ticket');
    }
    fileId = mintedFileId;
    ticket = mintedTicket;
  } catch (err) {
    patch({ status: UPLOAD_RECORD_STATUS.refused, error: messageOf(err) });
    return;
  }

  let sha256: string;
  try {
    sha256 = await deps.sha256(file);
  } catch (err) {
    patch({ status: UPLOAD_RECORD_STATUS.refused, error: messageOf(err) });
    return;
  }

  try {
    await deps.postBytes(ticket, file, sha256, (fraction) => patch({ progress: fraction }));
  } catch (err) {
    patch({ status: UPLOAD_RECORD_STATUS.refused, error: messageOf(err) });
    return;
  }

  // THE BYTES ARE NO LONGER IN FLIGHT, so the fraction stops — the sentence on `progress` above is
  // this module's promise to every page that renders one, and a page that reads it correctly
  // ("in flight" = a number) showed "Uploading 100%" for as long as a stored file was on screen
  // (ws b49jwta91, the statement picker). One of the two had to give; the promise is the one every
  // future page reads, so the behaviour is what moves.
  patch({ status: 'settling', progress: null });

  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    try {
      const polled = await deps.pollStatus(fileId);
      const recorded = readString(polled, 'status');
      if (recorded === UPLOAD_RECORD_STATUS.settled) {
        patch({ status: UPLOAD_RECORD_STATUS.settled, ref: fileId });
        return;
      }
      // A REFUSED RECORD IS AN ANSWER, not a reason to keep asking. The platform sniffed the bytes
      // and would not keep them, and no later poll can change that — spinning out the bound here
      // cost eight round trips and then reported a timeout for a verdict already given.
      if (recorded === UPLOAD_RECORD_STATUS.refused) {
        patch({ status: UPLOAD_RECORD_STATUS.refused, error: 'the platform would not keep this file' });
        return;
      }
    } catch {
      // A transient poll failure is not the verdict — the bound below is what gives up, not one
      // bad response.
    }
    await deps.sleep(Math.min(POLL_BASE_DELAY_MS * 2 ** attempt, POLL_MAX_DELAY_MS));
  }
  patch({ status: UPLOAD_RECORD_STATUS.refused, error: 'the upload never finished processing' });
}

const INITIAL: { status: UploadStatus; progress: number | null; ref: string | null; error: string | null } = {
  status: 'idle',
  progress: null,
  ref: null,
  error: null,
};

/**
 * Pick a file, watch it become a stored `ref`. `useSyncExternalStore` fits the OTHER platform reads
 * (one page-lifetime session, one config) because they are singletons every component shares; an
 * upload is not — two file inputs on one page pick independently — so this is ordinary component
 * state instead, wired to the plain pipeline above.
 */
export function useFileUpload(): FileUploadState {
  const [state, setState] = useState(INITIAL);
  const pick = useCallback(
    (file: File) => runFileUpload(file, (next) => setState((prev) => ({ ...prev, ...next }))),
    [],
  );
  const reset = useCallback(() => setState(INITIAL), []);
  return { ...state, pick, reset };
}
